import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { DollarSign } from "lucide-react";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

const fmt = (n) =>
  `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LocationProfitability() {
  const [period, setPeriod] = useState("week");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ signups: 0, revenue: 0, payroll: 0, net: 0 });

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/activity-locations/profitability?period=${period}`)
      .then((res) => {
        setRows(res.data?.rows || []);
        setTotals(res.data?.totals || { signups: 0, revenue: 0, payroll: 0, net: 0 });
      })
      .catch(() => {
        setRows([]);
        setTotals({ signups: 0, revenue: 0, payroll: 0, net: 0 });
      })
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <Card className="border-slate-200" data-testid="profitability-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-600" />
              Location Profitability
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Revenue = annualised donations (regular giving × 12) + one-off amounts. Payroll = Σ fundraiser pay-per-signup.
            </p>
          </div>
          <div className="flex gap-1" data-testid="profitability-period-buttons">
            {PERIODS.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? "default" : "outline"}
                onClick={() => setPeriod(p.value)}
                data-testid={`period-${p.value}`}
                className={period === p.value ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-center py-8 text-slate-400">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-center py-8 text-slate-400" data-testid="profitability-empty">
            No location activity in this period.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="profitability-totals">
              <div className="p-3 bg-slate-50 rounded-xl text-center">
                <div className="text-2xl font-bold text-slate-900">{totals.signups}</div>
                <div className="text-xs text-slate-500">Signups</div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl text-center">
                <div className="text-2xl font-bold text-emerald-700">{fmt(totals.revenue)}</div>
                <div className="text-xs text-slate-500">Est. Revenue</div>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl text-center">
                <div className="text-2xl font-bold text-amber-700">{fmt(totals.payroll)}</div>
                <div className="text-xs text-slate-500">Payroll Cost</div>
              </div>
              <div className={`p-3 rounded-xl text-center ${totals.net >= 0 ? "bg-blue-50" : "bg-red-50"}`}>
                <div className={`text-2xl font-bold ${totals.net >= 0 ? "text-blue-700" : "text-red-600"}`}>
                  {fmt(totals.net)}
                </div>
                <div className="text-xs text-slate-500">Net Score</div>
              </div>
            </div>

            <div className="overflow-x-auto" data-testid="profitability-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="py-2 pr-4 font-medium text-slate-600">Location</th>
                    <th className="py-2 pr-4 font-medium text-slate-600 text-right">Signups</th>
                    <th className="py-2 pr-4 font-medium text-slate-600 text-right">Est. Revenue</th>
                    <th className="py-2 pr-4 font-medium text-slate-600 text-right">Payroll Cost</th>
                    <th className="py-2 pr-4 font-medium text-slate-600 text-right">Net Score</th>
                    <th className="py-2 pr-4 font-medium text-slate-600 text-right">Cost / Signup</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.locationId}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`profitability-row-${r.locationId}`}
                    >
                      <td className="py-2 pr-4">
                        <div className="font-medium text-slate-900">{r.locationName}</div>
                        {r.locationAddress && (
                          <div className="text-xs text-slate-400">{r.locationAddress}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">{r.signups}</td>
                      <td className="py-2 pr-4 text-right font-mono text-emerald-700">
                        {fmt(r.revenue)}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-amber-700">
                        {fmt(r.payroll)}
                      </td>
                      <td
                        className={`py-2 pr-4 text-right font-mono font-semibold ${
                          r.net >= 0 ? "text-blue-700" : "text-red-600"
                        }`}
                      >
                        {fmt(r.net)}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-slate-600">
                        {fmt(r.costPerSignup)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
